import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box
} from '@mui/material';
import { Call, Save, Cancel } from '@mui/icons-material';
import { useGetMyProfileQuery } from '../../../../../services/ProfileService';
import { useCallLogForm } from './hooks';
import {
  CallRecipientField,
  CallDateField,
  CallAnsweredCheckbox,
  CallNotesField,
  CallerInfoAlert
} from './components';

interface AddCallLogDialogProps {
  open: boolean;
  onClose: () => void;
}

const AddCallLogDialog: React.FC<AddCallLogDialogProps> = ({ open, onClose }) => {
  const { data: profile } = useGetMyProfileQuery();
  const {
    formData,
    errors,
    isValid,
    updateField,
    handleSubmit,
    handleReset
  } = useCallLogForm(onClose);

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const onSubmit = () => {
    handleSubmit();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Call sx={{ color: '#3b82f6', fontSize: 24 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Add Call Log Entry
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
              Record details of a phone call related to this case
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 4 }}>
        <Box sx={{ pt: 2 }}>
          <Stack spacing={3}>
            <CallRecipientField
              value={formData.callTo}
              error={errors.callTo}
              onChange={(value) => updateField('callTo', value)}
            />

            <CallDateField
              value={formData.callDate}
              onChange={(value) => updateField('callDate', value)}
            />

            <CallAnsweredCheckbox
              checked={formData.isCallAnswered}
              onChange={(checked) => updateField('isCallAnswered', checked)}
            />

            <CallNotesField
              value={formData.notes}
              error={errors.notes}
              onChange={(value) => updateField('notes', value)}
            />

            <CallerInfoAlert callerName={profile?.name} />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc'
        }}
      >
        <Button
          onClick={handleClose}
          startIcon={<Cancel />}
          sx={{
            color: '#64748b',
            '&:hover': { backgroundColor: '#f1f5f9' }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          startIcon={<Save />}
          disabled={!isValid}
          sx={{
            backgroundColor: '#162346',
            fontWeight: 600,
            px: 3,
            '&:hover': { backgroundColor: '#1a2a52' }
          }}
        >
          Save Call Log
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCallLogDialog;
